import React, { useState } from 'react';
import { InboxMode } from './InboxMode';
import { InboundEmailMode } from './InboundEmailMode';
import { Tabs, ScreenHeader } from './ui';
import { IconInboxScan, IconForwardEmail } from './icons';
import type { ApiClient } from '../utils/api';
import type { InboxFile, InboundEmailFile } from '../types';

/**
 * Unified "Import from email" screen. Offers two equivalent paths:
 *
 *  1. Connect your inbox (OAuth read-only scan — Gmail, Outlook, Zoho)
 *  2. Forward a CAS (inbound email address)
 *
 * The user picks the mechanism they're more comfortable with; the caller
 * gets the same file-selected callback either way.
 */

export type EmailModeTab = 'CONNECT' | 'FORWARD';

interface EmailModeProps {
  apiKey: string;
  apiBaseUrl?: string;
  apiClient: ApiClient;
  inboxConfig?: React.ComponentProps<typeof InboxMode>['inboxConfig'];
  inboundEmailConfig?: React.ComponentProps<typeof InboundEmailMode>['config'];
  prefillPan?: string;
  /**
   * Pre-fill the password input on the inbound-email post-file screen.
   * Set by the parent when the user reached the inbound flow via the
   * generator handoff so they don't have to type the SDK's default
   * password.
   */
  prefillPassword?: string;
  enableInbox: boolean;
  enableInboundEmail: boolean;
  /** Whether the embedder has the mutual fund mailback flow enabled. */
  enableGenerator?: boolean;
  defaultTab?: EmailModeTab;
  onInboxFileSelect: (file: InboxFile, password: string) => void;
  onInboundFileSelect: (file: InboundEmailFile, password: string) => void;
  /**
   * Cross-flow from the empty-Gmail-inbox state — if the embedder also
   * has the mailback flow enabled, jump to it so the user can request a
   * fresh CAS instead of hitting a dead end.
   */
  onJumpToRequestCas?: () => void;
  /** Cross-flow: jump from the inbound flow back to the upload zone. */
  onJumpToUpload?: () => void;
  onBack: () => void;
  onEvent?: (event: string, metadata?: any) => void;
}

export const EmailMode: React.FC<EmailModeProps> = ({
  apiKey,
  apiBaseUrl,
  apiClient,
  inboxConfig,
  inboundEmailConfig,
  prefillPan,
  prefillPassword,
  enableInbox,
  enableInboundEmail,
  enableGenerator,
  defaultTab,
  onInboxFileSelect,
  onInboundFileSelect,
  onJumpToRequestCas,
  onJumpToUpload,
  onBack,
  onEvent,
}) => {
  // If only one mode is available, skip the tab UI entirely.
  const singleMode: EmailModeTab | null = !enableInbox && enableInboundEmail
    ? 'FORWARD'
    : enableInbox && !enableInboundEmail
    ? 'CONNECT'
    : null;

  const [tab, setTab] = useState<EmailModeTab>(
    singleMode || defaultTab || (enableInbox ? 'CONNECT' : 'FORWARD'),
  );

  const showTabs = !singleMode;

  return (
    <div>
      {showTabs && (
        <>
          <ScreenHeader
            icon={<IconInboxScan size={22} />}
            title="Import from your email"
            description="Two options: connect your inbox (Gmail, Outlook, Zoho — we find existing statements) or forward an email from any inbox"
          />
          <Tabs
            tabs={[
              {
                id: 'CONNECT',
                label: 'Connect inbox',
                icon: <IconInboxScan size={14} />,
              },
              {
                id: 'FORWARD',
                label: 'Forward email',
                icon: <IconForwardEmail size={14} />,
              },
            ]}
            activeId={tab}
            onChange={(id) => {
              setTab(id as EmailModeTab);
              onEvent?.('MODE_SWITCHED', { from: tab, to: id });
            }}
          />
        </>
      )}

      {tab === 'CONNECT' && enableInbox && (
        <InboxMode
          apiKey={apiKey}
          apiBaseUrl={apiBaseUrl}
          inboxConfig={inboxConfig}
          prefillPan={prefillPan}
          onFileSelect={onInboxFileSelect}
          onEvent={onEvent}
          onJumpToRequestCas={enableGenerator ? onJumpToRequestCas : undefined}
          onJumpToInbound={
            enableInboundEmail
              ? () => {
                  setTab('FORWARD');
                  onEvent?.('MODE_SWITCHED', {
                    from: 'CONNECT',
                    to: 'FORWARD',
                    reason: 'EMPTY_INBOX_ESCAPE',
                  });
                }
              : undefined
          }
        />
      )}
      {tab === 'FORWARD' && enableInboundEmail && (
        <InboundEmailMode
          client={apiClient}
          config={inboundEmailConfig}
          prefillPan={prefillPan}
          prefillPassword={prefillPassword}
          onFileSelect={onInboundFileSelect}
          onBack={onBack}
          onEvent={onEvent}
          onSwitchToGmail={
            enableInbox
              ? () => {
                  setTab('CONNECT');
                  onEvent?.('MODE_SWITCHED', { from: 'FORWARD', to: 'CONNECT' });
                }
              : undefined
          }
          onJumpToUpload={onJumpToUpload}
        />
      )}
    </div>
  );
};
